import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { supabase } from '../lib/supabase';
import SEOHead from './SEOHead';
import Header from './Header';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';
import NewsletterSignup from './NewsletterSignup';
import { getArticleStructuredData, getBreadcrumbStructuredData } from '../utils/structuredData';
import { Clock, ArrowLeft, ArrowRight, Linkedin, Twitter, Facebook, MessageCircle, Loader2, ChevronRight } from 'lucide-react';

interface Post {
  id: string;
  slug: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  content_es: string;
  content_en: string;
  featured_image: string | null;
  category_id: string | null;
  author_name: string;
  author_avatar: string | null;
  reading_time_minutes: number;
  published_at: string;
  updated_at: string;
  meta_title_es: string | null;
  meta_title_en: string | null;
  meta_description_es: string | null;
  meta_description_en: string | null;
  tags: string[];
}

interface RelatedPost {
  id: string;
  slug: string;
  title_es: string;
  title_en: string;
  excerpt_es: string;
  excerpt_en: string;
  featured_image: string | null;
  reading_time_minutes: number;
  published_at: string;
}

interface CategoryInfo {
  id: string;
  slug: string;
  name_es: string;
  name_en: string;
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const t = translations[language].blog;

  const [post, setPost] = useState<Post | null>(null);
  const [category, setCategory] = useState<CategoryInfo | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;

    const fetchPost = async () => {
      setLoading(true);
      setNotFound(false);

      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setPost(data);

      if (data.category_id) {
        const { data: catData } = await supabase
          .from('blog_categories')
          .select('id, slug, name_es, name_en')
          .eq('id', data.category_id)
          .maybeSingle();
        if (catData) setCategory(catData);

        const { data: related } = await supabase
          .from('blog_posts')
          .select('id, slug, title_es, title_en, excerpt_es, excerpt_en, featured_image, reading_time_minutes, published_at')
          .eq('is_published', true)
          .eq('category_id', data.category_id)
          .neq('id', data.id)
          .order('published_at', { ascending: false })
          .limit(3);

        if (related) setRelatedPosts(related);
      }

      setLoading(false);
    };

    fetchPost();
  }, [slug]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const title = post ? (language === 'es' ? post.title_es : post.title_en) : '';
  const excerpt = post ? (language === 'es' ? post.excerpt_es : post.excerpt_en) : '';
  const content = post ? (language === 'es' ? post.content_es : post.content_en) : '';
  const metaTitle = post ? (language === 'es' ? (post.meta_title_es || post.title_es) : (post.meta_title_en || post.title_en)) : '';
  const metaDescription = post ? (language === 'es' ? (post.meta_description_es || post.excerpt_es) : (post.meta_description_en || post.excerpt_en)) : '';

  const articleSchema = useMemo(() => {
    if (!post) return null;
    return getArticleStructuredData({
      title,
      description: excerpt,
      author: post.author_name,
      datePublished: post.published_at,
      dateModified: post.updated_at,
      image: post.featured_image || 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg',
      url: `https://laimu.mx/blog/${post.slug}`
    });
  }, [post, title, excerpt]);

  const breadcrumbSchema = useMemo(() => {
    if (!post) return null;
    return getBreadcrumbStructuredData([
      { name: 'Home', url: 'https://laimu.mx' },
      { name: 'Blog', url: 'https://laimu.mx/blog' },
      ...(category ? [{ name: language === 'es' ? category.name_es : category.name_en, url: `https://laimu.mx/blog/category/${category.slug}` }] : []),
      { name: title, url: `https://laimu.mx/blog/${post.slug}` }
    ]);
  }, [post, category, language, title]);

  const shareUrl = post ? `https://laimu.mx/blog/${post.slug}` : '';
  const shareTitle = encodeURIComponent(title);

  const socialLinks = [
    { icon: Linkedin, label: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, color: '#0A66C2' },
    { icon: Twitter, label: 'Twitter', url: `https://twitter.com/intent/tweet?text=${shareTitle}&url=${encodeURIComponent(shareUrl)}`, color: '#1DA1F2' },
    { icon: Facebook, label: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, color: '#1877F2' },
    { icon: MessageCircle, label: 'WhatsApp', url: `https://wa.me/?text=${shareTitle}%20${encodeURIComponent(shareUrl)}`, color: '#25D366' },
  ];

  if (loading) {
    return (
      <>
        <Header />
        <ScrollToTop />
        <div className="flex justify-center items-center py-32">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#115d7b' }} />
        </div>
        <Footer />
      </>
    );
  }

  if (notFound || !post) {
    return (
      <>
        <Header />
        <ScrollToTop />
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600 mb-8">{language === 'es' ? 'Artículo no encontrado.' : 'Article not found.'}</p>
          <a href="/blog" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold" style={{ backgroundColor: '#115d7b' }}>
            <ArrowLeft className="w-4 h-4" />
            {t.backToBlog}
          </a>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={metaTitle}
        description={metaDescription}
        image={post.featured_image || undefined}
        url={`/blog/${post.slug}`}
        type="article"
        structuredData={[articleSchema, breadcrumbSchema].filter(Boolean)}
      />
      <Header />
      <ScrollToTop />

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="container mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <a href="/" className="hover:text-teal-600 transition-colors">Home</a>
            <ChevronRight className="w-3.5 h-3.5" />
            <a href="/blog" className="hover:text-teal-600 transition-colors">Blog</a>
            {category && (
              <>
                <ChevronRight className="w-3.5 h-3.5" />
                <a href={`/blog/category/${category.slug}`} className="hover:text-teal-600 transition-colors">
                  {language === 'es' ? category.name_es : category.name_en}
                </a>
              </>
            )}
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-700 font-medium truncate max-w-xs">{title}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <article className="pt-8 pb-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            {category && (
              <a href={`/blog/category/${category.slug}`} className="inline-block mb-4">
                <span className="px-3 py-1.5 rounded-full text-sm font-semibold text-white" style={{ backgroundColor: '#ff8c00' }}>
                  {language === 'es' ? category.name_es : category.name_en}
                </span>
              </a>
            )}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">{title}</h1>
            <p className="text-lg text-gray-600 mb-6">{excerpt}</p>

            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-3">
                <img
                  src={post.author_avatar || `https://ui-avatars.com/api/?name=${post.author_name}&background=115d7b&color=fff&size=48`}
                  alt={post.author_name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-semibold text-gray-900">{post.author_name}</p>
                  <p className="text-sm text-gray-500">
                    {t.publishedOn} {formatDate(post.published_at)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-gray-400 text-sm">
                <Clock className="w-4 h-4" />
                {post.reading_time_minutes} {t.minutesRead}
              </div>
            </div>

            {/* Social Share */}
            <div className="flex items-center gap-3 py-4 border-y border-gray-100">
              <span className="text-sm font-medium text-gray-600">{t.shareArticle}:</span>
              {socialLinks.map(link => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full flex items-center justify-center bg-gray-100 hover:text-white transition-all duration-300 hover:scale-110"
                    style={{ ['--hover-color' as string]: link.color }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = link.color; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                    aria-label={link.label}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </article>

      {/* Featured Image */}
      {post.featured_image && (
        <div className="container mx-auto px-4 md:px-6 mb-12">
          <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-lg">
            <img
              src={post.featured_image}
              alt={title}
              className="w-full h-auto max-h-[400px] object-cover"
            />
          </div>
        </div>
      )}

      {/* Article Content */}
      <div className="container mx-auto px-4 md:px-6 pb-12">
        <div className="max-w-3xl mx-auto">
          <div
            className="prose prose-lg max-w-none
              prose-headings:text-gray-900 prose-headings:font-bold
              prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:my-4 prose-ol:my-4
              prose-li:text-gray-700 prose-li:mb-1
              prose-a:text-teal-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
              prose-strong:text-gray-900
              prose-blockquote:border-l-4 prose-blockquote:border-teal-500 prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>
      </div>

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="container mx-auto px-4 md:px-6 pb-8">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-500">{t.tags}:</span>
              {post.tags.map(tag => (
                <span key={tag} className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Demo CTA */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto rounded-2xl p-8 md:p-10 text-center" style={{ background: 'linear-gradient(135deg, #115d7b 0%, #0d4a63 100%)' }}>
            <h3 className="text-2xl font-bold text-white mb-3">{t.requestDemoCta}</h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">{t.requestDemoText}</p>
            <a
              href="/demo"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold text-white transition-all duration-300 hover:scale-105 hover:shadow-xl"
              style={{ backgroundColor: '#ff8c00' }}
            >
              {t.requestDemoCta}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t.relatedPosts}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map(rp => (
                <a
                  key={rp.id}
                  href={`/blog/${rp.slug}`}
                  className="group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white border border-gray-100 hover:-translate-y-1"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={rp.featured_image || 'https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&dpr=1'}
                      alt={language === 'es' ? rp.title_es : rp.title_en}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                      {language === 'es' ? rp.title_es : rp.title_en}
                    </h3>
                    <p className="text-gray-500 text-sm line-clamp-2">
                      {language === 'es' ? rp.excerpt_es : rp.excerpt_en}
                    </p>
                    <div className="flex items-center gap-1 text-gray-400 text-xs mt-3">
                      <Clock className="w-3 h-3" />
                      {rp.reading_time_minutes} {t.minutesRead}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="pb-12">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <NewsletterSignup variant="card" />
        </div>
      </section>

      {/* Back to Blog */}
      <div className="pb-12 text-center">
        <a href="/blog" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold border-2 transition-all duration-300 hover:shadow-lg" style={{ borderColor: '#115d7b', color: '#115d7b' }}>
          <ArrowLeft className="w-4 h-4" />
          {t.backToBlog}
        </a>
      </div>

      <Footer />
    </>
  );
};

export default BlogPost;
